import classNames from 'classnames';
import { isNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { type TextLayer } from '@meitu/saas-editor-core/es/layers/text-layer';
import { translateAngleToXY, translateLongSide, translateXYToAngle } from '@meitu/saas-editor-core/es/utils/math-kit';
import { LayerType, type IFontShadow, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import { ColorGroup } from '@meitu/xdesign-widgets';
import AngleInputGroup from '@/components/angle-input-group';
import { SimpleEffectPropsContainer } from '@/components/simple-effect-props-container';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { useLayerProps } from '@/panels/text-layer/hooks';
import { Tracker } from '@/panels/text-layer/utils';
import { type WidgetCommonProps } from '../../../types';
import './style.less';

type Props = WidgetCommonProps & {
    onOpenSubPanel: (subPanel: string, type?: string) => void;
};

const FALLBACK_COLOR = '#000000ff';

export const ShadowInspector: React.FC<Props> = ({ onOpenSubPanel, layerId, recordId, isIntl }) => {
    const { $t } = useLocaleStore();

    const editor = useEditor();
    const shadow = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const textShadow = layerData.textShadow || [];

        return textShadow.length > 0 ? textShadow[0] : null;
    }) as IFontShadow | null;

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const hidden = shadow ? Boolean(shadow.hidden) : true;
    const color = shadow ? shadow.color : FALLBACK_COLOR;
    const angle = shadow ? translateXYToAngle(shadow.x, shadow.y) : 0;
    const distance = shadow ? translateLongSide(shadow.x, shadow.y) : 0;

    // 由于 angle、distance 都是由 x、y 属性计算而来
    // 所以在修改某一项值的时候，都有可能影响到另外一项数据
    // 所以在组件内单独维护 angle、distance 的状态
    const [angleValue, setAngleValue] = useState(angle);
    const [distanceValue, setDistanceValue] = useState(distance);

    const add = async () => {
        if (shadow) return;
        const layer = await editor.getInstance<TextLayer>('TextLayer').addShadow(layerId);

        if (layer) {
            // ATTENTION: 文本支持批量操作，返回的 layer 可能是组图层
            const layerData =
                layer.layerType === LayerType.GROUP_LAYER
                    ? (layer.children[0].data as ITextLayerProps)
                    : (layer.data as ITextLayerProps);

            if (!layerData) return;
            const textShadow = layerData.textShadow[0];

            if (textShadow) {
                const initialAngle = Number(translateXYToAngle(textShadow.x, textShadow.y).toFixed(1));
                const initialDistance = Number(translateLongSide(textShadow.x, textShadow.y).toFixed(1));
                setAngleValue(initialAngle);
                setDistanceValue(initialDistance);
            }
        }

        Tracker.addShadow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'shortcut',
        });
    };

    const remove = () => {
        if (!shadow) return;
        editor.getInstance<TextLayer>('TextLayer').removeEffect(layerId, 'textShadow');
    };

    const setVisibility = (flag: boolean) => {
        if (!shadow) return;
        editor.getInstance<TextLayer>('TextLayer').setEffectVisibility(layerId, 'textShadow', flag);

        Tracker.toggleShadow({
            recordId,
            stageType: editor.services.editor.getCanvasType(),
            panelPosition: 'shortcut',
        });
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'shortcut',
                kind: 'textShadow',
            });
        }
    };

    const adjustColor = (value: string, options = { writeHistory: true }) => {
        if (!shadow) return;

        const payload = { idx: 0, data: { color: value } };
        editor.getInstance<TextLayer>('TextLayer').adjustShadowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'shortcut',
                prop: 'color',
                value,
            });
        }
    };

    // FIXME: distance 为 0 时，调整 angle 没有意义
    // 此时应该在 UI 上禁用，已和产品反馈，在下一个迭代中处理
    const adjustAngle = (value: number, options = { writeHistory: true }) => {
        if (!shadow || !isNumber(value)) return;

        if (distanceValue === 0) {
            setAngleValue(value);
            return;
        }

        const { x, y } = translateAngleToXY(value, distanceValue);
        const payload = { idx: 0, data: { x, y } };

        editor.getInstance<TextLayer>('TextLayer').adjustShadowProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustShadowProps({
                recordId,
                stageType: editor.services.editor.getCanvasType(),
                panelPosition: 'shortcut',
                prop: 'angle',
                value,
            });
        }
    };

    // 由于 angleValue、distanceValue 是 UI 层单独维护的状态
    // 所以需要根据 angle、distance 的变化进行同步
    useEffect(() => {
        setAngleValue(angle);
    }, [angle]);

    useEffect(() => {
        setDistanceValue(distance);
        if (distance === 0 && angleValue !== 0) {
            setAngleValue(angleValue);
        }
    }, [distance]);

    return (
        <SimpleEffectPropsContainer
            title={$t((v) => v.textStyleAdd.shadow)}
            showAdd={shadow == null}
            visible={!hidden}
            onAdd={add}
            onRemove={remove}
            onToggleVisible={() => setVisibility(!hidden)}
            onJump2SubPanel={() => onOpenSubPanel('textEffect', 'shadow')}
        >
            {shadow ? (
                <div
                    className={classNames('text-shadow-ctrl', {
                        'text-shadow-ctrl--disabled': hidden,
                    })}
                >
                    <div className="text-shadow-ctrl__color">
                        {isIntl ? null : (
                            <AngleInputGroup
                                angle={~~angleValue}
                                onAdjustAngle={(angle) => adjustAngle(angle, { writeHistory: false })}
                                onAdjustAngleAfter={(angle) => adjustAngle(angle, { writeHistory: true })}
                            />
                        )}
                        <ColorGroup
                            popupTriggerSize={{ width: 34, height: 34 }}
                            color={color}
                            setColor={(color) => adjustColor(color, { writeHistory: false })}
                            setColorAfter={(color, _, { isBrandColor }) => {
                                adjustColor(color, { writeHistory: true });

                                if (isBrandColor) {
                                    window._stat_track?.('hb_layers_func_use', {
                                        func_id: '1_107',
                                    });
                                }
                            }}
                            onOpenChange={handleColorPickerOpenChange}
                            showIcon={false}
                            popupOverlayClassName="effect-color-picker"
                            placement="left"
                            onToggleBizTab={(bizTab) => {
                                if (bizTab === 'team') {
                                    window._stat_track?.('hb_layers_func_click', {
                                        func_id: '1_107',
                                    });
                                }
                            }}
                        />
                    </div>
                </div>
            ) : null}
        </SimpleEffectPropsContainer>
    );
};
